// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useMemo } from 'react';
import { formatDate } from '@loa/theme';
import { ComingSoonModal, ViewLoAModal } from '@loa/ui-components';
import { clientListApi } from '../../api/clientListApi';
import type { ClientLOADTO, Page } from '../../types/clientList';
import { LOATableCard, EyeIcon, DownloadIcon } from '../loa';
import type { LOATableColumn, LOATableAction } from '../loa';
import './ClientLOAsTab.css';

type ClientLOAsTabProps = Readonly<{
  clientId: string;
}>;

function LOAStatusBadge({ status }: Readonly<{ status: string }>) {
  const getStatusDisplay = () => {
    switch (status) {
      case 'COMPLETED':
      case 'SIGNED':
        return { text: 'Signed', className: 'signed', showCheck: true };
      case 'PENDING_SIGNATURE':
      case 'PENDING':
        return { text: 'Pending', className: 'pending', showCheck: false };
      case 'VIEWED':
        return { text: 'Viewed', className: 'viewed', showCheck: false };
      case 'DECLINED':
        return { text: 'Declined', className: 'declined', showCheck: false };
      case 'DRAFT':
        return { text: 'Draft', className: 'draft', showCheck: false };
      case 'EXPIRED':
        return { text: 'Expired', className: 'expired', showCheck: false };
      case 'VOIDED':
        return { text: 'Voided', className: 'voided', showCheck: false };
      default:
        return { text: status, className: 'default', showCheck: false };
    }
  };

  const { text, className, showCheck } = getStatusDisplay();

  return (
    <span className={`loa-status-badge ${className}`}>
      {showCheck && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
      )}
      {text}
    </span>
  );
}

export function ClientLOAsTab({ clientId }: ClientLOAsTabProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const [data, setData] = useState<Page<ClientLOADTO> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [showComingSoonAlert, setShowComingSoonAlert] = useState(false);
  const [viewPdfState, setViewPdfState] = useState<{ loaId: string; title: string } | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const loadLOAs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await clientListApi.getClientLOAs(clientId, currentPage - 1, itemsPerPage);
        setData(response);
      } catch (err) {
        console.error('Failed to load LOAs:', err);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLOAs();
  }, [clientId, currentPage, itemsPerPage]);

  const handleDownload = async (loaId: string, loaStatus: string) => {
    if (loaStatus !== 'COMPLETED') {
      setShowComingSoonAlert(true);
      return;
    }

    try {
      setDownloadingId(loaId);
      const blob = await clientListApi.downloadLOA(loaId);

      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `LOA-${loaId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error('Error downloading LOA:', error);
      alert(error.response?.data?.message || 'Failed to download LOA');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewPdf = async (loa: ClientLOADTO) => {
    const title = loa.profileName || `LOA ${loa.loaId}`;
    setViewPdfState({ loaId: loa.loaId, title });
    setPdfLoading(true);
    setPdfUrl(null);
    try {
      const blob = await clientListApi.downloadLOA(loa.loaId);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch {
      // Error handled by keeping pdfUrl null — modal shows "Document not available"
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewPdfState(null);
  };

  const loas = data?.content || [];
  const totalPages = data?.totalPages || 1;

  const columns = useMemo<LOATableColumn<ClientLOADTO>[]>(() => [
    {
      key: 'provider',
      label: 'Provider',
      render: (loa) => <span style={{ fontWeight: 500 }}>{loa.profileName || '-'}</span>,
    },
    {
      key: 'policyType',
      label: 'Policy type',
      render: (loa) => `${loa.policyCount} ${loa.policyCount === 1 ? 'Policy' : 'Policies'}`,
    },
    {
      key: 'lastUpdated',
      label: 'Last updated',
      render: (loa) => formatDate(loa.signedAt || loa.completedAt || loa.createdAt),
    },
    {
      key: 'ownership',
      label: 'Ownership',
      render: () => '-',
    },
    {
      key: 'signatories',
      label: 'Signatories',
      render: (loa) => String(loa.policyCount),
    },
    {
      key: 'status',
      label: 'Status',
      render: (loa) => <LOAStatusBadge status={loa.signatoryStatus || loa.loaStatus} />,
    },
  ], []);

  const actions = useMemo<LOATableAction<ClientLOADTO>[]>(() => [
    {
      key: 'view',
      icon: <EyeIcon />,
      label: 'View LoA details',
      onClick: (loa) => { void handleViewPdf(loa); },
      visible: (loa) => loa.loaStatus === 'COMPLETED',
    },
    {
      key: 'download',
      icon: <DownloadIcon />,
      label: 'Download LOA',
      onClick: (loa) => { void handleDownload(loa.loaId, loa.loaStatus); },
      disabled: () => !!downloadingId,
      loading: (loa) => downloadingId === loa.loaId,
    },
  ], [downloadingId]);

  return (
    <>
      <LOATableCard
        data={loas}
        columns={columns}
        actions={actions}
        keyExtractor={(loa) => loa.loaId}
        loading={isLoading}
        error={error?.message || null}
        showUploadButton
        onUploadClick={() => setShowComingSoonAlert(true)}
        pagination={{
          currentPage,
          totalPages,
          itemsPerPage,
          itemsPerPageOptions: [5, 8, 10, 20],
          onPageChange: setCurrentPage,
          onItemsPerPageChange: setItemsPerPage,
        }}
      />

      <ComingSoonModal
        open={showComingSoonAlert}
        onClose={() => setShowComingSoonAlert(false)}
      />

      <ViewLoAModal
        open={!!viewPdfState}
        onClose={handleClosePdfViewer}
        documentUrl={pdfUrl}
        title={viewPdfState?.title || ''}
        loading={pdfLoading}
        downloadFilename={viewPdfState ? `LOA-${viewPdfState.loaId}.pdf` : undefined}
      />
    </>
  );
}
